import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Navigation, Search, MapPin, Phone, Star, Loader2, ChevronRight, Share2, Locate, Instagram, Facebook, History, Trash2, Filter, Clock, Wifi, WifiOff, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import AdBanner from '../components/AdBanner';
import VoiceInterfaceCombined from '../components/VoiceInterfaceCombined';
import { useActiveAssistant } from '../hooks/useActiveAssistant';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social Media Icons (SVG components)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const CATEGORIES = [
  // === MÉDECINE GÉNÉRALE ===
  { id: 'general', name: 'Médecin Généraliste', icon: '👨‍⚕️' },
  { id: 'doctor', name: 'Médecin', icon: '🩺' },
  
  // === SERVICES DE SANTÉ ===
  { id: 'pharmacy', name: 'Pharmacie', icon: '💊' },
  { id: 'hospital', name: 'Hôpital', icon: '🏥' },
  { id: 'clinic', name: 'Clinique', icon: '🏨' },
  { id: 'laboratory', name: 'Laboratoire', icon: '🔬' },
  { id: 'emergency', name: 'Urgences', icon: '🚑' },
  
  // === DENTAIRE ===
  { id: 'dentist', name: 'Dentiste', icon: '🦷' },
  { id: 'orthodontist', name: 'Orthodontiste', icon: '😁' },
  { id: 'dental_surgeon', name: 'Chirurgien-Dentiste / Stomatologiste', icon: '🦷' },
  
  // === SPÉCIALISTES MÉDICAUX ===
  { id: 'internist', name: 'Interniste / Médecine Interne', icon: '🏥' },
  { id: 'ophthalmologist', name: 'Ophtalmologue', icon: '👁️' },
  { id: 'pediatrician', name: 'Pédiatre', icon: '👶' },
  { id: 'gynecologist', name: 'Gynécologue', icon: '🤰' },
  { id: 'obstetrician', name: 'Obstétricien', icon: '🤱' },
  { id: 'cardiologist', name: 'Cardiologue', icon: '❤️' },
  { id: 'dermatologist', name: 'Dermatologue', icon: '🧴' },
  { id: 'neurologist', name: 'Neurologue', icon: '🧠' },
  { id: 'psychiatrist', name: 'Psychiatre', icon: '🧠' },
  { id: 'psychologist', name: 'Psychologue', icon: '💭' },
  { id: 'ent', name: 'ORL (Oto-Rhino-Laryngologue)', icon: '👂' },
  { id: 'gastroenterologist', name: 'Gastro-entérologue', icon: '🫃' },
  { id: 'pulmonologist', name: 'Pneumologue', icon: '🫁' },
  { id: 'rheumatologist', name: 'Rhumatologue', icon: '🦴' },
  { id: 'endocrinologist', name: 'Endocrinologue', icon: '🩸' },
  { id: 'urologist', name: 'Urologue', icon: '🔬' },
  { id: 'nephrologist', name: 'Néphrologue', icon: '🫘' },
  { id: 'allergist', name: 'Allergologue', icon: '🤧' },
  { id: 'immunologist', name: 'Immunologue', icon: '🛡️' },
  { id: 'infectiologist', name: 'Infectiologue', icon: '🦠' },
  { id: 'oncologist', name: 'Oncologue', icon: '🎗️' },
  { id: 'hematologist', name: 'Hématologue', icon: '🩸' },
  { id: 'geriatrician', name: 'Gériatre', icon: '👴' },
  { id: 'physiatrist', name: 'Médecin Rééducateur (MPR)', icon: '🏃' },
  { id: 'sports_medicine', name: 'Médecin du Sport', icon: '⚽' },
  { id: 'emergency_medicine', name: 'Médecin Urgentiste', icon: '🚨' },
  { id: 'occupational_medicine', name: 'Médecin du Travail', icon: '🏢' },
  { id: 'pathologist', name: 'Anatomo-Pathologiste', icon: '🔬' },
  { id: 'medical_geneticist', name: 'Généticien Médical', icon: '🧬' },
  { id: 'nuclear_medicine', name: 'Médecine Nucléaire', icon: '☢️' },
  
  // === CHIRURGIENS ===
  { id: 'surgeon', name: 'Chirurgien Général', icon: '⚕️' },
  { id: 'plastic_surgeon', name: 'Chirurgien Plasticien / Esthétique', icon: '✨' },
  { id: 'vascular_surgeon', name: 'Chirurgien Vasculaire', icon: '🫀' },
  { id: 'neurosurgeon', name: 'Neurochirurgien', icon: '🧠' },
  { id: 'cardiac_surgeon', name: 'Chirurgien Cardiaque', icon: '❤️' },
  { id: 'orthopedist', name: 'Chirurgien Orthopédiste', icon: '🦴' },
  { id: 'thoracic_surgeon', name: 'Chirurgien Thoracique', icon: '🫁' },
  { id: 'digestive_surgeon', name: 'Chirurgien Digestif', icon: '🫃' },
  { id: 'urological_surgeon', name: 'Chirurgien Urologue', icon: '🔬' },
  { id: 'anesthesiologist', name: 'Anesthésiste-Réanimateur', icon: '💉' },
  
  // === PARAMÉDICAL - RÉÉDUCATION ===
  { id: 'physiotherapist', name: 'Kinésithérapeute', icon: '💪' },
  { id: 'osteopath', name: 'Ostéopathe', icon: '🤲' },
  { id: 'chiropractor', name: 'Chiropracteur', icon: '🦴' },
  { id: 'speech_therapist', name: 'Orthophoniste', icon: '🗣️' },
  { id: 'occupational_therapist', name: 'Ergothérapeute', icon: '🛠️' },
  { id: 'psychomotor_therapist', name: 'Psychomotricien', icon: '🎯' },
  
  // === PARAMÉDICAL - SOINS ===
  { id: 'nurse', name: 'Infirmier(ère)', icon: '💉' },
  { id: 'midwife', name: 'Sage-Femme', icon: '👶' },
  { id: 'dietitian', name: 'Diététicien(ne) / Nutritionniste', icon: '🥗' },
  
  // === PODOLOGIE & PÉDICURE ===
  { id: 'podiatrist', name: 'Podologue', icon: '🦶' },
  { id: 'pedicurist', name: 'Pédicure', icon: '🦶' },
  { id: 'pedicure_podiatrist', name: 'Pédicure-Podologue', icon: '🦶' },
  
  // === BIEN-ÊTRE & MÉDECINES DOUCES ===
  { id: 'reflexologist', name: 'Réflexologue', icon: '🦶' },
  { id: 'acupuncturist', name: 'Acupuncteur', icon: '📍' },
  { id: 'naturopath', name: 'Naturopathe', icon: '🌿' },
  { id: 'homeopath', name: 'Homéopathe', icon: '💧' },
  { id: 'sophrologist', name: 'Sophrologue', icon: '🧘' },
  { id: 'hypnotherapist', name: 'Hypnothérapeute', icon: '🌀' },
  
  // === ESTHÉTIQUE & BEAUTÉ ===
  { id: 'aesthetician', name: 'Esthéticien(ne)', icon: '💅' },
  { id: 'aesthetic_doctor', name: 'Médecin Esthétique', icon: '✨' },
  
  // === OPTIQUE & AUDITION ===
  { id: 'optician', name: 'Opticien', icon: '👓' },
  { id: 'audiologist', name: 'Audioprothésiste', icon: '👂' },
  
  // === IMAGERIE ===
  { id: 'radiologist', name: 'Radiologue', icon: '📷' },
  { id: 'mri', name: 'IRM', icon: '🔬' },
  { id: 'scanner', name: 'Scanner', icon: '📡' },
  { id: 'ultrasound', name: 'Échographie', icon: '📺' },
  
  // === PHARMACIE & LABORATOIRE ===
  { id: 'biologist', name: 'Biologiste Médical', icon: '🧪' },
];

const Professionals = () => {
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('France');
  const [name, setName] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    openNow: false,
    hasPhone: false,
    maxDistance: null // in km
  });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const activeAssistantId = useActiveAssistant();
  const { t } = useLanguage();

  useEffect(() => {
    loadFavorites();
    loadRecentSearches();
    
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      toast.success('Connexion rétablie');
    };
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning('Vous êtes hors ligne. Certaines fonctionnalités peuvent être limitées.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load recent searches from localStorage
  const loadRecentSearches = () => {
    try {
      const saved = localStorage.getItem('drbooking_recent_searches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading recent searches:', e);
    }
  };

  // Save search to recent searches
  const saveRecentSearch = (searchData) => {
    try {
      const newSearch = {
        id: Date.now(),
        category: searchData.category,
        categoryName: CATEGORIES.find(c => c.id === searchData.category)?.name || searchData.category,
        categoryIcon: CATEGORIES.find(c => c.id === searchData.category)?.icon || '🏥',
        city: searchData.city,
        country: searchData.country,
        name: searchData.name || '',
        timestamp: new Date().toISOString()
      };
      
      // Keep only last 10 unique searches
      const filtered = recentSearches.filter(s => 
        !(s.category === newSearch.category && s.city === newSearch.city && s.country === newSearch.country)
      );
      const updated = [newSearch, ...filtered].slice(0, 10);
      
      setRecentSearches(updated);
      localStorage.setItem('drbooking_recent_searches', JSON.stringify(updated));
    } catch (e) {
      console.error('Error saving recent search:', e);
    }
  };

  // Apply a recent search
  const applyRecentSearch = (search) => {
    setCategory(search.category);
    setCity(search.city);
    setCountry(search.country);
    setName(search.name || '');
    setShowRecentSearches(false);
    toast.info(`Recherche chargée: ${search.categoryName} à ${search.city}`);
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('drbooking_recent_searches');
    toast.success('OK');
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        
        // Reverse geocode to get city name
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=fr`
          );
          const data = await response.json();
          const cityName = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || '';
          const countryName = data.address?.country || 'France';
          
          if (cityName) {
            setCity(cityName);
            // Map country names
            const countryMap = {
              'France': 'France',
              'Maroc': 'Maroc',
              'Morocco': 'Maroc',
              'Luxembourg': 'Luxembourg',
              'Belgique': 'Belgique',
              'Belgium': 'Belgique',
              'Suisse': 'Suisse',
              'Switzerland': 'Suisse',
              'Allemagne': 'Allemagne',
              'Germany': 'Allemagne',
              'Espagne': 'Espagne',
              'Spain': 'Espagne',
              'Italie': 'Italie',
              'Italy': 'Italie',
              'Portugal': 'Portugal'
            };
            setCountry(countryMap[countryName] || 'France');
            toast.success(`📍 Position détectée: ${cityName}`);
          }
        } catch (e) {
          toast.success('📍 Position GPS détectée');
        }
        setGeoLoading(false);
      },
      (error) => {
        setGeoLoading(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Vous avez refusé la géolocalisation');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Position non disponible');
            break;
          case error.TIMEOUT:
            toast.error('Délai de géolocalisation dépassé');
            break;
          default:
            toast.error('Erreur de géolocalisation');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Sort results by distance if user location is available
  const sortResultsByDistance = (resultsList) => {
    if (!userLocation) return resultsList;
    
    return resultsList.map(result => ({
      ...result,
      distance: result.latitude && result.longitude 
        ? calculateDistance(userLocation.latitude, userLocation.longitude, result.latitude, result.longitude)
        : null
    })).sort((a, b) => {
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
  };

  // Apply advanced filters to results
  const applyFilters = (resultsList) => {
    let filtered = [...resultsList];
    
    // Filter by open now
    if (filters.openNow) {
      filtered = filtered.filter(r => r.open_now === true);
    }
    
    // Filter by has phone
    if (filters.hasPhone) {
      filtered = filtered.filter(r => r.phone && r.phone.trim() !== '');
    }
    
    // Filter by max distance
    if (filters.maxDistance && userLocation) {
      filtered = filtered.filter(r => 
        r.distance !== null && r.distance <= filters.maxDistance
      );
    }
    
    return filtered;
  };

  // Share functions
  const shareOnWhatsApp = (result) => {
    const text = `🏥 Je te recommande: ${result.name}\n📍 ${result.address || ''}\n📞 ${result.phone || ''}\n\nTrouvé sur drbooking`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnFacebook = (result) => {
    const text = `Je recommande ${result.name} - ${result.address || ''} - Trouvé sur drbooking`;
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnInstagram = () => {
    // Instagram doesn't have a direct share URL, redirect to profile
    window.open('https://www.instagram.com/drbooking.pro', '_blank');
    toast.info('Suivez-nous sur Instagram @drbooking.pro');
  };

  const shareOnTikTok = () => {
    // TikTok doesn't have a direct share URL, redirect to profile
    window.open('https://www.tiktok.com/@drbooking.pro', '_blank');
    toast.info('Suivez-nous sur TikTok @drbooking.pro');
  };

  const loadFavorites = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await axios.get(`${API_URL}/api/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(res.data.favorites || []);
      } catch (e) {}
    }
  };

  const handleSearch = async () => {
    if (!category || !city) {
      toast.error('Veuillez sélectionner une catégorie et une ville');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = { category, city, country, limit: 20 };
      if (name.trim()) {
        params.name = name.trim();
      }
      
      const res = await axios.get(`${API_URL}/api/search/healthcare`, { params });
      let resultsList = res.data.results || [];
      
      // Sort by distance if user location is available
      if (userLocation) {
        resultsList = sortResultsByDistance(resultsList);
      }
      
      // Apply advanced filters
      resultsList = applyFilters(resultsList);
      
      setResults(resultsList);
      
      // Save to recent searches
      saveRecentSearch({ category, city, country, name: name.trim() });
      
      // Log search for analytics (fire and forget)
      axios.post(`${API_URL}/api/search/log`, {
        query: name.trim(),
        specialty: category,
        city: city,
        country: country,
        search_type: 'manual',
        results_count: resultsList.length,
        appointment_booked: false
      }).catch(() => {});
      
      if (resultsList.length === 0) {
        toast.info(t('no_results'));
      }
    } catch (error) {
      if (isOffline) {
        toast.warning('Recherche hors ligne - résultats limités');
      } else {
        toast.error('Erreur de recherche');
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Navigation intelligente : propose le choix Waze ou Google Maps
  const openNavigation = (result) => {
    const lat = result.latitude;
    const lng = result.longitude;
    const address = result.address || `${result.name} ${city}`;
    
    // Détection mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    // URL Google Maps (fonctionne toujours)
    const googleMapsUrl = lat && lng 
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    
    if (isMobile && lat && lng) {
      // Sur mobile avec coordonnées : proposer le choix
      if (isIOS) {
        // iOS : utiliser le scheme waze://
        const wazeUrl = `waze://?ll=${lat},${lng}&navigate=yes`;
        
        // Créer un lien temporaire pour Waze
        const wazeLink = document.createElement('a');
        wazeLink.href = wazeUrl;
        
        // Essayer d'ouvrir Waze
        const wazeClicked = Date.now();
        wazeLink.click();
        
        // Si pas de réponse après 1.5s, ouvrir Google Maps
        setTimeout(() => {
          // Si l'utilisateur est toujours sur la page, Waze n'a pas marché
          if (document.visibilityState === 'visible' && Date.now() - wazeClicked < 2000) {
            window.open(googleMapsUrl, '_blank');
          }
        }, 1500);
        
      } else if (isAndroid) {
        // Android : essayer intent Waze, sinon Google Maps directement
        const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes&z=10`;
        
        // Ouvrir Waze dans une nouvelle fenêtre
        const wazeOpened = window.open(wazeUrl, '_blank');
        
        // Fallback immédiat si popup bloqué
        if (!wazeOpened) {
          window.open(googleMapsUrl, '_blank');
        }
      }
    } else {
      // Sur desktop ou sans coordonnées : Google Maps directement
      window.open(googleMapsUrl, '_blank');
    }
    
    toast.success('Navigation lancée !');
  };

  // Ouvrir directement Google Maps (pour bouton alternatif)
  const openGoogleMaps = (result) => {
    const lat = result.latitude;
    const lng = result.longitude;
    const address = result.address || `${result.name} ${city}`;
    
    const googleMapsUrl = lat && lng 
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    
    window.open(googleMapsUrl, '_blank');
    toast.success('Google Maps ouvert !');
  };

  // Garder openWaze pour compatibilité (utilise maintenant openNavigation)
  const openWaze = (result) => {
    openNavigation(result);
  };

  const addToFavorites = async (result) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/favorites`, {
        name: result.name || result.full_name,
        category: category,
        address: result.address,
        phone: result.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ajouté aux favoris !');
      loadFavorites();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const getCategoryIcon = (catId) => {
    const cat = CATEGORIES.find(c => c.id === catId);
    return cat?.icon || '🏥';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl mb-4 shadow-lg shadow-blue-500/30">
            <Search className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {t('search_professional')}
          </h1>
          <p className="text-slate-400 text-sm">
            Trouvez et naviguez vers le professionnel de santé
          </p>
        </div>

        {/* Search Form */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-4 space-y-4">
            {/* Name Search */}
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Nom du professionnel (optionnel)</label>
              <Input
                placeholder="Ex: Dr. Martin, Cabinet Dentaire..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="search-name-input"
              />
            </div>
            
            {/* Category */}
            <div>
              <label className="text-slate-400 text-sm mb-1 block">{t('specialty')}</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="search-category-select">
                  <SelectValue placeholder="Choisir une spécialité" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-slate-700">
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* City & Country */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-400 text-sm mb-1 block">{t('city')}</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: Paris, Casablanca..."
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white flex-1"
                    data-testid="search-city-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={getUserLocation}
                    disabled={geoLoading}
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 shrink-0"
                    title="Utiliser ma position"
                    data-testid="geolocation-btn"
                  >
                    {geoLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Locate className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-1 block">{t('country')}</label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="search-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="France" className="text-white">🇫🇷 France</SelectItem>
                    <SelectItem value="Maroc" className="text-white">🇲🇦 Maroc</SelectItem>
                    <SelectItem value="Luxembourg" className="text-white">🇱🇺 Luxembourg</SelectItem>
                    <SelectItem value="Belgique" className="text-white">🇧🇪 Belgique</SelectItem>
                    <SelectItem value="Suisse" className="text-white">🇨🇭 Suisse</SelectItem>
                    <SelectItem value="Allemagne" className="text-white">🇩🇪 Allemagne</SelectItem>
                    <SelectItem value="Espagne" className="text-white">🇪🇸 Espagne</SelectItem>
                    <SelectItem value="Italie" className="text-white">🇮🇹 Italie</SelectItem>
                    <SelectItem value="Portugal" className="text-white">🇵🇹 Portugal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Geolocation Status */}
            {userLocation && (
              <div className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 px-3 py-2 rounded-lg">
                <Locate className="w-4 h-4" />
                <span>Position GPS active - Les résultats seront triés par distance</span>
              </div>
            )}

            {/* Offline Status */}
            {isOffline && (
              <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 px-3 py-2 rounded-lg">
                <WifiOff className="w-4 h-4" />
                <span>Mode hors ligne - Résultats limités au cache</span>
              </div>
            )}

            {/* Advanced Filters Toggle */}
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center justify-between w-full text-slate-400 text-sm hover:text-white transition-colors py-2"
              data-testid="advanced-filters-toggle"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtres avancés
                {(filters.openNow || filters.hasPhone || filters.maxDistance) && (
                  <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {[filters.openNow, filters.hasPhone, filters.maxDistance].filter(Boolean).length}
                  </span>
                )}
              </span>
              {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="space-y-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                {/* Open Now Filter */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.openNow}
                    onChange={(e) => setFilters({...filters, openNow: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                    data-testid="filter-open-now"
                  />
                  <span className="flex items-center gap-2 text-slate-300 text-sm">
                    <Clock className="w-4 h-4 text-green-400" />
                    Ouvert maintenant
                  </span>
                </label>

                {/* Has Phone Filter */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.hasPhone}
                    onChange={(e) => setFilters({...filters, hasPhone: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                    data-testid="filter-has-phone"
                  />
                  <span className="flex items-center gap-2 text-slate-300 text-sm">
                    <Phone className="w-4 h-4 text-blue-400" />
                    Avec numéro de téléphone
                  </span>
                </label>

                {/* Max Distance Filter */}
                {userLocation && (
                  <div className="space-y-2">
                    <label className="text-slate-400 text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Distance maximale
                    </label>
                    <div className="flex gap-2">
                      {[5, 10, 20, 50].map(km => (
                        <button
                          key={km}
                          type="button"
                          onClick={() => setFilters({...filters, maxDistance: filters.maxDistance === km ? null : km})}
                          className={`px-3 py-1 text-xs rounded-full transition-colors ${
                            filters.maxDistance === km 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                          }`}
                          data-testid={`filter-distance-${km}`}
                        >
                          {km} km
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clear Filters */}
                {(filters.openNow || filters.hasPhone || filters.maxDistance) && (
                  <button
                    type="button"
                    onClick={() => setFilters({ openNow: false, hasPhone: false, maxDistance: null })}
                    className="text-red-400 text-sm hover:text-red-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            )}

            {/* Search Button */}
            <Button 
              onClick={handleSearch} 
              disabled={loading || isOffline}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              data-testid="search-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recherche...</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> {t('search_professional')}</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Recent Searches & Favorites Toggle */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {recentSearches.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowRecentSearches(!showRecentSearches)}
              className="border-purple-500/50 text-purple-400"
              data-testid="recent-searches-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Recherches récentes ({recentSearches.length})
            </Button>
          )}
          {favorites.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowFavorites(!showFavorites)}
              className="border-yellow-500/50 text-yellow-400"
            >
              <Star className="w-4 h-4 mr-2 fill-yellow-400" />
              Mes Favoris ({favorites.length})
            </Button>
          )}
        </div>

        {/* Recent Searches List */}
        {showRecentSearches && recentSearches.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">🕐 Recherches récentes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearRecentSearches}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Effacer
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {recentSearches.map((search) => (
                <button
                  key={search.id}
                  onClick={() => applyRecentSearch(search)}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg hover:border-purple-500 transition-colors text-left"
                  data-testid={`recent-search-${search.id}`}
                >
                  <span className="text-xl">{search.categoryIcon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{search.categoryName}</p>
                    <p className="text-slate-400 text-xs truncate">
                      {search.city}, {search.country}
                      {search.name && ` • "${search.name}"`}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Favorites List */}
        {showFavorites && (
          <div className="mb-6 space-y-3">
            <h3 className="text-white font-semibold mb-2">⭐ Mes Favoris</h3>
            {favorites.map((fav, idx) => (
              <Card key={fav.id || idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCategoryIcon(fav.category)}</span>
                      <div>
                        <p className="text-white font-medium">{fav.name}</p>
                        {fav.address && <p className="text-slate-400 text-sm">{fav.address}</p>}
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => openWaze(fav)}
                    >
                      <Navigation className="w-4 h-4 mr-1" /> Y aller
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Results */}
        {searched && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">
                {results.length > 0 ? `${results.length} résultat(s)` : 'Aucun résultat'}
              </h3>
              {results.length > 0 && (
                <span className="text-slate-500 text-xs">Données: Google Places</span>
              )}
            </div>

            {results.map((result, idx) => (
              <Card key={result.id || idx} className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{getCategoryIcon(category)}</span>
                        <h4 className="text-white font-semibold">{result.name || result.full_name}</h4>
                        {result.distance !== null && result.distance !== undefined && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                            {result.distance < 1 
                              ? `${Math.round(result.distance * 1000)} m` 
                              : `${result.distance.toFixed(1)} km`}
                          </span>
                        )}
                      </div>
                      
                      {result.address && (
                        <div className="flex items-center gap-1 text-slate-400 text-sm mb-1">
                          <MapPin className="w-3 h-3" />
                          <span className="line-clamp-1">{result.address}</span>
                        </div>
                      )}
                      
                      {result.phone && (
                        <div className="flex items-center gap-1 text-slate-400 text-sm mb-2">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${result.phone}`} className="hover:text-white">{result.phone}</a>
                        </div>
                      )}

                      {/* Social Share Buttons */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-slate-500 text-xs">Partager:</span>
                        <button
                          onClick={() => shareOnWhatsApp(result)}
                          className="p-1.5 rounded-full bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                          title="Partager sur WhatsApp"
                          data-testid={`share-whatsapp-${idx}`}
                        >
                          <WhatsAppIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => shareOnFacebook(result)}
                          className="p-1.5 rounded-full bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors"
                          title="Partager sur Facebook"
                          data-testid={`share-facebook-${idx}`}
                        >
                          <Facebook className="w-4 h-4" />
                        </button>
                        <button
                          onClick={shareOnInstagram}
                          className="p-1.5 rounded-full bg-pink-600/20 text-pink-400 hover:bg-pink-600/30 transition-colors"
                          title="Instagram"
                          data-testid={`share-instagram-${idx}`}
                        >
                          <Instagram className="w-4 h-4" />
                        </button>
                        <button
                          onClick={shareOnTikTok}
                          className="p-1.5 rounded-full bg-slate-600/20 text-slate-300 hover:bg-slate-600/30 transition-colors"
                          title="TikTok"
                          data-testid={`share-tiktok-${idx}`}
                        >
                          <TikTokIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => openWaze(result)}
                      >
                        <Navigation className="w-4 h-4 mr-1" /> Y aller
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm"
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                        onClick={() => addToFavorites(result)}
                      >
                        <Star className="w-4 h-4 mr-1" /> Favori
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {results.length === 0 && !loading && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center">
                  <Search className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">{t('no_results')}</p>
                  <p className="text-slate-500 text-sm mt-1">Essayez une autre ville ou spécialité</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Quick Categories (when no search yet) */}
        {!searched && (
          <div>
            <h3 className="text-white font-semibold mb-3">Catégories populaires</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {CATEGORIES.slice(0, 8).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setCategory(cat.id);
                    toast.info(`${cat.name} sélectionné. Entrez une ville.`);
                  }}
                  className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-blue-500 transition-colors text-center"
                >
                  <span className="text-2xl block mb-1">{cat.icon}</span>
                  <span className="text-white text-xs">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Powered by */}
        <div className="mt-8 text-center space-y-4">
          {/* Social Media Follow Section */}
          <div className="bg-slate-800/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-3">Suivez drbooking sur les réseaux sociaux</p>
            <div className="flex justify-center gap-3">
              <a
                href="https://wa.me/352621411475"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
                data-testid="social-whatsapp"
              >
                <WhatsAppIcon className="w-5 h-5" />
                <span className="text-sm">WhatsApp</span>
              </a>
              <a
                href="https://www.instagram.com/drbooking.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-pink-600/20 text-pink-400 rounded-lg hover:bg-pink-600/30 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
                <span className="text-sm">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/drbooking.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
                <span className="text-sm">Facebook</span>
              </a>
              <a
                href="https://www.tiktok.com/@drbooking.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-slate-600/20 text-slate-300 rounded-lg hover:bg-slate-600/30 transition-colors"
                data-testid="social-tiktok"
              >
                <TikTokIcon className="w-5 h-5" />
                <span className="text-sm">TikTok</span>
              </a>
            </div>
          </div>
          
          <p className="text-slate-500 text-xs">
            🗺️ Recherche par Google Places • 🧭 Navigation Waze/Google Maps • 📍 Géolocalisation
          </p>
        </div>
      </div>
      
      {/* Voice Assistant Floating Button with Fallback */}
      <VoiceInterfaceCombined 
        assistantId={activeAssistantId}
        onCallStart={() => console.log('Voice call started')}
        onCallEnd={() => console.log('Voice call ended')}
        onError={(e) => toast.error('Erreur assistant vocal: ' + e.message)}
      />
    </div>
  );
};

export default Professionals;
